import { useEffect, useRef, useState } from "react";
import { useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import * as turf from "@turf/turf";

export const CLUSTER_ZOOM = 8; // zoom < 8 → clusters; zoom >= 8 → markers individuales

const norm = (s) => String(s || "").trim().toUpperCase();

// Escala: Azul cielo → Índigo → Púrpura → Rosa
const COLOR_SCALE = [
  { t: 0,    color: [226, 232, 240] }, // slate-200   (sin mercados)
  { t: 0.15, color: [186, 230, 253] }, // sky-200
  { t: 0.35, color: [165, 180, 252] }, // indigo-200
  { t: 0.55, color: [129, 140, 248] }, // indigo-400
  { t: 0.75, color: [236,  72, 153] }, // pink-500
  { t: 1.0,  color: [190,  24,  93] }, // pink-800
];

function interpolateColor(t) {
  if (t <= 0) return COLOR_SCALE[0].color;
  if (t >= 1) return COLOR_SCALE[COLOR_SCALE.length - 1].color;
  let lo = COLOR_SCALE[0], hi = COLOR_SCALE[1];
  for (let i = 1; i < COLOR_SCALE.length; i++) {
    if (t <= COLOR_SCALE[i].t) { lo = COLOR_SCALE[i - 1]; hi = COLOR_SCALE[i]; break; }
  }
  const f = (t - lo.t) / (hi.t - lo.t);
  return lo.color.map((c, i) => Math.round(c + f * (hi.color[i] - c)));
}

const CLUSTER_SIZE = 28;

function createClusterIcon(count, max) {
  const t = max > 0 ? count / max : 0;
  const [r, g, b] = interpolateColor(t);
  // Halo exterior con el mismo color pero semitransparente
  const halo = `0 0 0 3px #fff, 0 0 0 7px rgba(${r},${g},${b},0.30)`;
  return L.divIcon({
    className: "",
    iconSize: [CLUSTER_SIZE, CLUSTER_SIZE],
    iconAnchor: [CLUSTER_SIZE / 2, CLUSTER_SIZE / 2],
    html: `
      <div class="dept-cluster-icon" style="
        width:${CLUSTER_SIZE}px; height:${CLUSTER_SIZE}px;
        background: rgb(${r},${g},${b});
        font-size: 13px;
        box-shadow: ${halo};
      ">${count}</div>
    `,
  });
}

const DepartmentClusterLayer = ({ points, zoom, onZoomChange }) => {
  const map = useMap();
  const entriesRef    = useRef({}); // depName → { marker, feature }
  const countsRef     = useRef({});
  const maxRef        = useRef(1);
  const pointsRef     = useRef(points);
  const [geoReady, setGeoReady] = useState(false);

  // Mantener pointsRef sincronizado con la prop
  useEffect(() => { pointsRef.current = points; }, [points]);

  // Escuchar zoom y notificar al padre
  useMapEvents({
    zoomend: (e) => onZoomChange && onZoomChange(e.target.getZoom()),
  });

  // Cargar GeoJSON, calcular centroides y crear markers (una sola vez)
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const mod = await import("../data/geojson/dpto.json");
      const urlOrData = mod.default ?? mod;
      let geoData;
      if (typeof urlOrData === "string") {
        const res = await fetch(urlOrData);
        geoData = await res.json();
      } else {
        geoData = urlOrData;
      }
      if (cancelled || !geoData?.features) return;

      geoData.features.forEach((feature) => {
        const depName = norm(feature.properties?.departamento);
        if (!depName) return;

        // Centroide con turf
        const centroidFeature = turf.centroid(feature);
        const [lng, lat] = centroidFeature.geometry.coordinates;

        const marker = L.marker([lat, lng], {
          icon: createClusterIcon(0, 1),
          interactive: true,
          zIndexOffset: 1000,
        });

        // Tooltip siempre visible con el nombre del departamento
        marker.bindTooltip(
          `<div class="dept-cluster-tooltip"><b>${feature.properties.departamento}</b></div>`,
          { direction: "top", offset: [0, -6], opacity: 1 }
        );

        marker.on("click", () => {
          const deptPoints = (pointsRef.current || []).filter(
            (p) => norm(p.departamento) === depName &&
                   p.latitud != null && p.longitud != null
          );

          if (deptPoints.length > 0) {
            const latLngs = deptPoints.map((p) => [p.latitud, p.longitud]);
            const bounds = L.latLngBounds(latLngs);
            map.flyToBounds(bounds, { padding: [80, 80], maxZoom: 13, duration: 0.6, easeLinearity: 0.25 });
            return;
          }

          // Fallback: bounds del polígono
          const bounds = L.geoJSON(feature).getBounds();
          map.flyToBounds(bounds, { padding: [40, 40], maxZoom: 10, duration: 0.6, easeLinearity: 0.25 });
        });

        entriesRef.current[depName] = { marker, feature };
      });

      setGeoReady(true); // dispara el efecto de actualización
    };

    load().catch((e) => console.error("[DeptCluster] Error:", e));

    return () => {
      cancelled = true;
      Object.values(entriesRef.current).forEach(({ marker }) => {
        if (map.hasLayer(marker)) map.removeLayer(marker);
      });
      entriesRef.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Actualizar iconos Y visibilidad — se dispara cuando el GeoJSON está listo,
  // cuando cambian los puntos filtrados o cuando el usuario hace zoom.
  useEffect(() => {
    if (!geoReady) return;

    const counts = {};
    (points || []).forEach((item) => {
      const dep = norm(item.departamento);
      if (dep) counts[dep] = (counts[dep] || 0) + 1;
    });
    const max = Math.max(1, ...Object.values(counts));
    countsRef.current = counts;
    maxRef.current    = max;

    const shouldShow = zoom < CLUSTER_ZOOM;

    Object.entries(entriesRef.current).forEach(([depName, { marker }]) => {
      const count = counts[depName] || 0;
      if (count > 0) marker.setIcon(createClusterIcon(count, max));

      if (shouldShow && count > 0) {
        if (!map.hasLayer(marker)) marker.addTo(map);
      } else {
        if (map.hasLayer(marker)) map.removeLayer(marker);
      }
    });
  }, [geoReady, points, zoom, map]);

  return null;
};

export default DepartmentClusterLayer;
